const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const arrayLength = (value) => (Array.isArray(value) ? value.length : 0);

const changedWhenProvided = (incoming, key, previous) => (
    hasOwn(incoming, key) && incoming[key] !== previous
);

/**
 * Decide how a locally recorded frame history should react to accepted socket
 * state. A remote action cannot be reconstructed reliably, so the local record
 * must be labelled incomplete. A new/reset frame must first discard the old
 * record so events from two frames are never mixed.
 */
export const classifyRemoteFrameUpdate = (previous, incoming) => {
    const startProvided = hasOwn(incoming, 'frame_start_timestamp');
    const frameNumberChanged = changedWhenProvided(incoming, 'current_frame', previous.currentFrame);
    // Older clients do not send frame_start_timestamp. A frame-number change
    // still means the previous frame's timestamp must not leak into the next
    // frame, so treat the missing value as a reset rather than "unchanged".
    const incomingStart = startProvided
        ? incoming.frame_start_timestamp
        : (frameNumberChanged ? null : previous.frameStartTimestamp);
    const frameStartChanged = startProvided && incomingStart !== previous.frameStartTimestamp;
    const reset = frameNumberChanged || frameStartChanged;

    const scoreOrActionChanged = [
        ['first_player_score', previous.player1Score],
        ['second_player_score', previous.player2Score],
        ['reds_remaining', previous.redsRemaining],
        ['current_player', previous.currentPlayer],
        ['switch_count', previous.switchCount],
        ['free_ball_active', previous.freeBallActive],
    ].some(([key, value]) => changedWhenProvided(incoming, key, value))
        || (hasOwn(incoming, 'first_player_potted_balls')
            && arrayLength(incoming.first_player_potted_balls) !== arrayLength(previous.player1PottedBalls))
        || (hasOwn(incoming, 'second_player_potted_balls')
            && arrayLength(incoming.second_player_potted_balls) !== arrayLength(previous.player2PottedBalls));

    // After a reset, zeroed state is a clean next frame. If the accepted state
    // already contains play, this client missed the opening remote actions.
    const hasStarted = incomingStart !== null && incomingStart !== undefined;
    const newFrameAlreadyInProgress = hasStarted
        || Number(incoming.first_player_score || 0) !== 0
        || Number(incoming.second_player_score || 0) !== 0
        || arrayLength(incoming.first_player_potted_balls) > 0
        || arrayLength(incoming.second_player_potted_balls) > 0
        || Number(incoming.switch_count || 0) > 0;

    return {
        reset,
        incomplete: reset ? newFrameAlreadyInProgress : scoreOrActionChanged,
    };
};
